import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2, Save, Download, Send, Hammer, Package } from "lucide-react";
import { api, money, downloadPdf, garageVatRate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState } from "@/components/States";
import { NumberPlate } from "@/components/NumberPlate";
import { PartPicker } from "@/components/PartPicker";
import { toast } from "sonner";

const STATUSES = ["draft", "sent", "accepted", "rejected", "expired"];

export default function QuotationEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ title: "", valid_until: "", status: "draft", notes: "", items: [], assessment_notes: "", important_note: "", mileage: "", category: "" });
  const [catGroups, setCatGroups] = useState([]);
  const [garage, setGarage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => { api("/jobs/categories").then((d) => setCatGroups(d.groups || [])).catch(() => {}); }, []);
  useEffect(() => { api("/garage/settings").then(setGarage).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const doc = await api(`/quotations/${id}`);
      setQ(doc);
      setForm({
        title: doc.title || "", valid_until: doc.valid_until || "", status: doc.status || "draft",
        notes: doc.notes || "", items: (doc.items || []).map((i) => ({ ...i })),
        assessment_notes: doc.assessment_notes || "", important_note: doc.important_note || "",
        mileage: doc.mileage != null ? String(doc.mileage) : "", category: doc.category || "",
      });
      if (doc.customer_id) api(`/customers/${doc.customer_id}`).then(setCustomer).catch(() => {});
      if (doc.vehicle_id) api(`/vehicles/${doc.vehicle_id}`).then(setVehicle).catch(() => {});
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const net = form.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const vatPct = garageVatRate(garage);
  const vat = net * (vatPct / 100);
  const upd = (i, k, v) => { const c = [...form.items]; c[i] = { ...c[i], [k]: v }; setForm({ ...form, items: c }); };
  const addItem = (kind) => setForm({ ...form, items: [...form.items, { description: "", qty: 1, unit_price: "", kind }] });
  const [pickerOpen, setPickerOpen] = useState(false);
  const addFromStock = (p) => setForm((prev) => ({ ...prev, items: [...prev.items, { description: p.name, qty: 1, unit_price: Number(p.unit_price) || 0, kind: "part" }] }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.category && !form.title.trim()) { toast.error("Work category is required"); return; }
    const catLabel = (() => {
      for (const g of catGroups) {
        const c = (g.categories || g.items || []).find((x) => x.id === form.category);
        if (c) return c.label || c.name;
      }
      return form.title.trim();
    })();
    setSaving(true);
    try {
      await api(`/quotations/${id}`, { method: "PATCH", body: JSON.stringify({
        title: catLabel || form.title.trim(), valid_until: form.valid_until || null, status: form.status,
        notes: form.notes || "", items: form.items.filter((i) => i.description.trim()).map((i) => ({ ...i, qty: Number(i.qty) || 0, unit_price: Number(i.unit_price) || 0 })),
        assessment_notes: form.assessment_notes || "", important_note: form.important_note || "",
        mileage: form.mileage ? Number(form.mileage) : null, category: form.category || null,
      }) });
      toast.success("Quotation updated");
      load();
    } catch (e2) { toast.error(e2.message); } finally { setSaving(false); }
  };

  const downloadQuotePdf = async () => {
    try { await downloadPdf(`/quotations/${id}/pdf`, `quotation-${q?.quotation_number || id}.pdf`); }
    catch (e) { toast.error(e.message || "Could not generate PDF"); }
  };
  const sendQuote = async () => {
    setSending(true);
    try {
      const res = await api(`/quotations/${id}/send`, { method: "POST" });
      if (res.email_delivered) toast.success(`Quotation emailed to ${res.to}`);
      else if (!res.email_configured) toast.success(`Prepared for ${res.to} (email activates once deployed)`);
      else toast.success(`Quotation sent to ${res.to}`);
      load();
    } catch (e) { toast.error(e.message || "Could not send quotation"); } finally { setSending(false); }
  };
  const convertToJob = async () => {
    setConverting(true);
    try {
      const res = await api(`/quotations/${id}/convert-to-job`, { method: "POST" });
      toast.success("Job created from quotation");
      if (res.job_id) navigate(`/jobs/${res.job_id}`);
    } catch (e) { toast.error(e.message || "Could not convert to job"); } finally { setConverting(false); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const companyLabel = customer?.customer_type === "company" || customer?.company_name ? customer?.company_name : null;

  return (
    <div className="mx-auto max-w-2xl">
      <Link to={`/quotations/${id}`} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Back to quotation</Link>
      <PageHeader title={q?.quotation_number || "Edit Quotation"} subtitle={companyLabel || customer?.name}
        actions={<>
          <button onClick={downloadQuotePdf} data-testid="quote-pdf-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><Download className="h-4 w-4" />PDF</button>
          <button onClick={sendQuote} disabled={sending || !customer?.email} title={customer?.email ? `Email to ${customer.email}` : "No email on file"} data-testid="quote-send-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-bold text-midnight hover:bg-secondary disabled:opacity-50">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Send</button>
          <button onClick={convertToJob} disabled={converting} data-testid="quote-convert-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-3 py-2.5 text-sm font-bold text-white disabled:opacity-60">{converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Hammer className="h-4 w-4" />}Convert to Job</button>
        </>} />
      <form onSubmit={save} className="space-y-4 rounded-xl border border-border bg-card p-6">
        {(vehicle || customer) && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg bg-secondary p-3 text-sm">
            {vehicle && <NumberPlate vrm={vehicle.vrm} size="sm" />}
            <span className="font-semibold text-midnight">{vehicle ? `${vehicle.make} ${vehicle.model}` : ""}</span>
            {customer && <span className="text-muted-foreground">· {companyLabel ? `${companyLabel} (${customer.name})` : customer.name}{customer.vat_registered && customer.vat_number ? ` · VAT ${customer.vat_number}` : ""}</span>}
          </div>
        )}
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">ASSESSMENT NOTES</span>
          <textarea value={form.assessment_notes} onChange={(e) => setForm({ ...form, assessment_notes: e.target.value })} data-testid="quote-edit-assessment" rows={2} placeholder="What was inspected / found" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">WORK CATEGORY *</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="quote-edit-category" className="w-full rounded-lg border border-input px-3.5 py-3 text-base outline-none focus:border-brand">
              <option value="">{form.title ? `Current: ${form.title}` : "Select category…"}</option>
              {catGroups.map((g) => (
                <optgroup key={g.id || g.label} label={g.label || g.name}>
                  {(g.categories || g.items || []).map((c) => <option key={c.id} value={c.id}>{c.label || c.name}</option>)}
                </optgroup>
              ))}
            </select></label>
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">VALID UNTIL</span>
            <input type="date" value={form.valid_until ? form.valid_until.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="quote-edit-valid" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">MILEAGE</span>
            <input type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} data-testid="quote-edit-mileage" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
          <label className="block sm:col-span-2"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">STATUS</span>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="quote-edit-status" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand">
              {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}</select></label>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between"><span className="text-[11px] font-bold tracking-widest text-muted-foreground">LINE ITEMS (use a negative price for a discount)</span>
            <div className="flex gap-2">
              <button type="button" onClick={() => addItem("labour")} className="text-sm font-bold text-brand-2">+ Labour</button>
              <button type="button" onClick={() => addItem("part")} className="text-sm font-bold text-brand-2">+ Part</button>
              <button type="button" onClick={() => setPickerOpen(true)} data-testid="quote-add-from-stock" className="inline-flex items-center gap-1 text-sm font-bold text-brand-2"><Package className="h-4 w-4" />From stock</button>
            </div>
          </div>
          <div className="space-y-2">
            {form.items.map((it, i) => (
              <div key={i} className="flex gap-2" data-testid={`quote-edit-item-${i}`}>
                <input value={it.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Description" data-testid={`quote-edit-desc-${i}`} className="min-w-0 flex-1 rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
                <select value={it.kind} onChange={(e) => upd(i, "kind", e.target.value)} className="rounded-md border border-input px-1 py-2 text-xs outline-none"><option value="labour">Labour</option><option value="part">Part</option></select>
                <input type="number" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="w-14 rounded-md border border-input px-2 py-2 text-center text-sm outline-none focus:border-brand" />
                <input type="number" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className="w-24 rounded-md border border-input px-2 py-2 text-right text-sm outline-none focus:border-brand" />
                <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })} className="text-danger"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">NOTES</span>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quote-edit-notes" rows={2} className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>

        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">IMPORTANT NOTE</span>
          <textarea value={form.important_note} onChange={(e) => setForm({ ...form, important_note: e.target.value })} data-testid="quote-edit-important" rows={2} placeholder="Shown at the bottom of the quotation" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>

        <div className="flex justify-end gap-6 border-t border-border pt-3 text-sm">
          <span className="text-muted-foreground">Net {money(net)}</span>
          {vatPct > 0 && <span className="text-muted-foreground">VAT {money(vat)}</span>}
          <span className="font-display text-lg font-bold text-midnight">{money(net + vat)}</span>
        </div>

        <button type="submit" disabled={saving} data-testid="quote-edit-save-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4" />SAVE CHANGES</>}
        </button>
      </form>
      <PartPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={addFromStock} />
    </div>
  );
}
